import { useState, useEffect } from 'react';
import { Printer, Zap, Loader2, AlertTriangle, MonitorCheck, Save, Cpu, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import printProvider from '@/services/printProvider';
import API_URL from '@/utils/api';

export function PrinterSettings() {
    const [customerPrinter, setCustomerPrinter] = useState('');
    const [kitchenPrinter, setKitchenPrinter] = useState('');
    const [testing, setTesting] = useState(null);
    const [bridgeStatus, setBridgeStatus] = useState('checking');
    const [agents, setAgents] = useState([]);
    const [loadingAgents, setLoadingAgents] = useState(true);

    useEffect(() => {
        setCustomerPrinter(printProvider.getCustomerPrinter());
        setKitchenPrinter(printProvider.getKitchenPrinter());
        checkBridge();
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        setLoadingAgents(true);
        const token = localStorage.getItem('session_token');


        try {
            const res = await fetch(`${API_URL}/agent/list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setAgents(data);
            }
        } catch (e) {
            console.error("Error fetching agents:", e);
        } finally {
            setLoadingAgents(false);
        }
    };

    const checkBridge = async () => {
        setBridgeStatus('checking');
        try {
            const bridgeUrl = printProvider.getPrinterServerUrl();
            const res = await fetch(bridgeUrl, { method: 'OPTIONS' });
            if (res.ok) setBridgeStatus('online');
            else setBridgeStatus('offline');
        } catch (e) {
            setBridgeStatus('offline');
        }
    };

    const saveSettings = () => {
        printProvider.setCustomerPrinter(customerPrinter);
        printProvider.setKitchenPrinter(kitchenPrinter);
        toast.success("Configuración guardada correctamente");
    };

    const testPrint = async (type) => {
        setTesting(type);
        const mockOrder = {
            order_id: 'PRUEBA-' + Math.floor(1000 + Math.random() * 9000),
            items: [{ name: 'TEST DE IMPRESIÓN', quantity: 1, price: 0.00 }],
            total: 0,
            created_at: new Date().toISOString(),
            is_preview: true
        };

        const res = await printProvider.printTicket(mockOrder, 'Conexo POS', type);
        if (res.success) {
            toast.success(`Ticket enviado (${res.method === 'agent' ? 'Agente 🤖' : 'Bridge 🔌'})`);
        } else {
            toast.error(res.error);
        }
        setTesting(null);
    };

    return (
        <div className="space-y-6 py-2">
            {/* Print Agent Status (Modern Mode) */}
            <div className={`p-4 rounded-xl border flex flex-col gap-3 transition-colors bg-blue-900/10 border-blue-800`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Cpu className="text-blue-400" />
                        <div>
                            <p className="text-sm font-bold text-white">Print Agent (Súper Estable)</p>
                            <p className="text-xs text-blue-500">Impresión en la nube + polling local</p>
                        </div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={fetchAgents} className="h-8 w-8 text-gray-400">
                        <Zap className={`w-4 h-4 ${loadingAgents ? 'animate-spin' : ''}`} />
                    </Button>
                </div>

                <div className="space-y-2">
                    {loadingAgents ? (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Loader2 className="w-3 h-3 animate-spin" /> Buscando agentes activos...
                        </div>
                    ) : agents.length > 0 ? (
                        agents.map(agent => (
                            <div key={agent.agent_id} className="flex items-center justify-between bg-gray-900/40 p-2 rounded-lg border border-blue-900/20">
                                <span className="text-xs text-gray-200">{agent.name}</span>
                                <div className="flex items-center gap-1">
                                    <span className={`w-2 h-2 rounded-full ${agent.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-gray-600'}`}></span>
                                    <span className="text-[10px] text-gray-400 uppercase font-bold">{agent.status || 'offline'}</span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-[10px] text-orange-400 italic">No hay agentes registrados. El sistema usará el Bridge local como fallback.</p>
                    )}
                </div>
            </div>

            {/* Legacy Bridge Status */}
            <div className={`p-4 rounded-xl border flex items-center justify-between transition-colors
                ${bridgeStatus === 'online' ? 'border-gray-800 bg-gray-900/20' : 'border-red-900/30 bg-red-900/5'}`}>
                <div className="flex items-center gap-3">
                    <MonitorCheck className={bridgeStatus === 'online' ? 'text-green-500' : 'text-gray-600'} />
                    <div>
                        <p className="text-sm font-semibold text-gray-300">Legacy Bridge (Puerto 8001)</p>
                        <p className={`text-[10px] ${bridgeStatus === 'online' ? 'text-green-600' : 'text-gray-600'}`}>
                            {bridgeStatus === 'online' ? 'Detectado y disponible' : 'No detectado (Opcional)'}
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-4 bg-gray-900/40 p-4 rounded-xl border border-gray-800">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-800 pb-2">Configuración de Impresoras</p>

                <div className="space-y-3">
                    <label className="text-xs font-semibold text-gray-400 flex items-center gap-2">
                        <Printer className="w-4 h-4" /> IMPRESORA TICKETS (CLIENTE)
                    </label>
                    <input
                        type="text"
                        value={customerPrinter}
                        onChange={(e) => setCustomerPrinter(e.target.value)}
                        placeholder="Ej: POS-80C"
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-600 outline-none"
                    />
                </div>

                <div className="space-y-3">
                    <label className="text-xs font-semibold text-gray-400 flex items-center gap-2">
                        <Printer className="w-4 h-4" /> IMPRESORA COMANDAS (COCINA)
                    </label>
                    <input
                        type="text"
                        value={kitchenPrinter}
                        onChange={(e) => setKitchenPrinter(e.target.value)}
                        placeholder="Ej: POS-80C"
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-orange-600 outline-none"
                    />
                </div>

                <Button onClick={saveSettings} className="w-full bg-blue-700 hover:bg-blue-600 text-white font-bold h-10 shadow-lg shadow-blue-900/20">
                    <Save className="w-4 h-4 mr-2" /> GUARDAR CONFIGURACIÓN
                </Button>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
                <Button
                    onClick={() => testPrint('customer')}
                    disabled={!!testing}
                    variant="outline"
                    className="border-gray-800 text-gray-400 hover:bg-blue-900/20 hover:text-blue-400 h-10 font-bold"
                >
                    {testing === 'customer' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Probar Cliente'}
                </Button>
                <Button
                    onClick={() => testPrint('kitchen')}
                    disabled={!!testing}
                    variant="outline"
                    className="border-gray-800 text-gray-400 hover:bg-orange-900/20 hover:text-orange-400 h-10 font-bold"
                >
                    {testing === 'kitchen' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Probar Cocina'}
                </Button>
            </div>

            <div className="p-3 bg-blue-900/5 rounded-lg border border-blue-900/20">
                <p className="text-[10px] text-blue-400 leading-tight">
                    <Zap className="w-3 h-3 inline mr-1" />
                    <strong>Nota:</strong> El sistema prioriza el <strong>Print Agent</strong>. Si no hay ningún agente activo, intentará usar el <strong>Bridge local</strong> automáticamente.
                </p>
            </div>
        </div>
    );
}

export default PrinterSettings;

